import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, RefreshCw, Store, User, IdCard, PhoneCall, MailOpen, Bot, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { api, createAgencyClient } from "@/lib/api";
import { ApiProvider } from "@/lib/apiClient";
import { useAgencyWorkspace } from "@/context/AgencyWorkspaceContext";
import { getAgencyOperationalBadge, getAiBadge } from "@/lib/agencyBadges";
import { fmtTime } from "@/lib/callbacks";
import CallbackModal from "@/components/CallbackModal";
import ContactDrawer from "@/pages/admin/agency/ContactDrawer";
import { AiStatusControl } from "@/components/agency/AiStatusControl";
import { MessageList } from "@/components/whatsapp/Conversation";

// Tre stati distinti: mittente non collegato ≠ nessun template approvato.
const LEAD_TPL_REASON = {
  RECEPTIONIST_WHATSAPP_NOT_CONNECTED: "Il WhatsApp della receptionist assegnata non è collegato.",
  RECEPTIONIST_NOT_ASSIGNED: "Nessuna receptionist assegnata: impossibile inviare.",
  WHATSAPP_NOT_CONNECTED: "Il WhatsApp del salone non è collegato.",
  TEMPLATE_FETCH_FAILED: "Non è stato possibile leggere i template da WhatsApp. Riprova fra poco.",
  NO_APPROVED_TEMPLATES_ON_WABA: "Non ci sono template approvati su questo account WhatsApp.",
};

function fmt(iso) {  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function TenantChip({ name }) {
  return <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]">{name}</span>;
}
function Badge({ badge }) {
  if (!badge) return null;
  const { label, Icon, cls } = badge;
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold ${cls}`}><Icon className="h-2.5 w-2.5" />{label}</span>;
}

function apiErr(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(", ") || fallback;
  if (d && typeof d === "object") return d.msg || fallback;
  return fallback;
}
function tplBody(t) {
  const c = (t?.components || []).find((x) => (x.type || "").toUpperCase() === "BODY");
  return c?.text || t?.name || "";
}

export default function AgencyLeadInboxPage() {
  const { availableTenants } = useAgencyWorkspace();
  const [view, setView] = useState("lead"); // lead | customer
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salon, setSalon] = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [sel, setSel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showCallback, setShowCallback] = useState(false);
  const [cbByClient, setCbByClient] = useState({});
  const [windowOpen, setWindowOpen] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showTpl, setShowTpl] = useState(false);
  const [pendingTpl, setPendingTpl] = useState(null);
  const [pendingTplLang, setPendingTplLang] = useState("");
  const [pendingTplVars, setPendingTplVars] = useState([]);
  const [tplReason, setTplReason] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    const url = view === "customer" ? "/agency/customer-conversations" : "/agency/lead-conversations";
    try { const r = await api.get(url); setRows(r.data || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  }, [view]);
  const loadCallbacks = useCallback(async () => {
    try {
      const r = await api.get("/agency/callbacks?status=scheduled");
      const now = Date.now();
      const m = {};
      (r.data || []).forEach((cb) => {
        if (new Date(cb.scheduled_at).getTime() >= now && !m[cb.client_id]) m[cb.client_id] = cb;
      });
      setCbByClient(m);
    } catch { setCbByClient({}); }
  }, []);
  useEffect(() => { load(); loadCallbacks(); const t = setInterval(() => { load(); loadCallbacks(); }, 15000); return () => clearInterval(t); }, [load, loadCallbacks]);

  // Client axios legato al tenant DELLA conversazione (non al selectedTenant globale).
  const convClient = useMemo(() => (sel ? createAgencyClient(() => sel.tenant?.id) : null), [sel]);

  const openConv = useCallback(async (row) => {
    setSel({ ...row, unread: false });
    setMessages([]); setShowTpl(false);
    setRows((prev) => prev.map((x) => (x.thread_key === row.thread_key && x.tenant?.id === row.tenant?.id ? { ...x, unread: false } : x)));
    const c = createAgencyClient(() => row.tenant?.id);
    try { const r = await c.get(`/whatsapp/messages/${row.thread_key}`); setMessages(r.data?.messages || []); setWindowOpen(!!r.data?.window_open); }
    catch { setMessages([]); }
    c.post("/inbox/threads/mark-read", { channel: "whatsapp", client_id: row.thread_key }).catch(() => {});
    try {
      const t = await c.get(`/whatsapp/sendable-templates?client_id=${row.thread_key}`);
      setTemplates(t.data?.templates || []);
      setTplReason(t.data?.reason || null);
      setPendingTpl(null); setPendingTplLang(""); setPendingTplVars([]);
    } catch { setTemplates([]); }
  }, []);

  // Deep-link dalla Panoramica: ?client=<thread_key> → apre la conversazione.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const want = params.get("client");
    if (!want || sel || !rows.length) return;
    const row = rows.find((r) => r.thread_key === want);
    if (row) { openConv(row); params.delete("client"); setParams(params, { replace: true }); }
  }, [params, setParams, rows, sel, openConv]);

  useEffect(() => {
    if (!sel || !convClient) return;
    const t = setInterval(async () => {
      try { const r = await convClient.get(`/whatsapp/messages/${sel.thread_key}`); setMessages(r.data?.messages || []); setWindowOpen(!!r.data?.window_open); }
      catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(t);
  }, [sel, convClient]);

  const send = async () => {
    if (!reply.trim() || !convClient) return;
    if (pendingTpl && pendingTplVars.some((v) => !v.trim())) {
      toast.error("Compila tutte le variabili del template"); return;
    }
    setSending(true);
    try {
      const payload = pendingTpl
        ? { client_id: sel.thread_key, body: reply, template_name: pendingTpl,
            params: { language: pendingTplLang,
              ...(pendingTplVars.length ? { components: [{ type: "BODY",
                parameters: pendingTplVars.map((v) => ({ type: "text", text: v })) }] } : {}) } }
        : { client_id: sel.thread_key, body: reply.trim() };
      await convClient.post("/whatsapp/send", payload);
      setReply(""); setPendingTpl(null); setPendingTplLang(""); setPendingTplVars([]);
      const r = await convClient.get(`/whatsapp/messages/${sel.thread_key}`);
      setMessages(r.data?.messages || []); setWindowOpen(!!r.data?.window_open);
      if (sel.ai_status === "active") setSel((s) => ({ ...s, ai_status: "paused" }));
      load();
      toast.success(pendingTpl ? "Template inviato" : "Messaggio inviato");
    } catch (e) {
      toast.error(apiErr(e, "Invio non riuscito"));
    } finally { setSending(false); }
  };

  const markUnread = async () => {
    if (!convClient || !sel) return;
    try { await convClient.post("/inbox/threads/mark-unread", { channel: "whatsapp", client_id: sel.thread_key }); setSel((s) => ({ ...s, unread: true })); toast.success("Segnato come non letto"); load(); }
    catch (e) { toast.error(apiErr(e, "Operazione non riuscita")); }
  };
  const toggleAi = async () => {
    if (!sel?.agency_case_id) return;
    const next = sel.ai_status === "active" ? "disabled" : "active";
    setAiBusy(true);
    try { await api.patch(`/agency/cases/${sel.agency_case_id}`, { ai_status: next }); setSel((s) => ({ ...s, ai_status: next })); load(); toast.success(next === "active" ? "AI attivata" : "AI disattivata"); }
    catch (e) { toast.error(apiErr(e, "Operazione non riuscita")); }
    finally { setAiBusy(false); }
  };
  const selectTemplate = (t) => {
    setPendingTpl(t.name);
    setPendingTplLang(t.language || "");
    setPendingTplVars(Array(new Set((t.variables || [])
      .filter((v) => (v.component || "").toUpperCase() === "BODY")
      .map((v) => v.position)).size).fill(""));
    setReply(tplBody(t) || t.name);
    setShowTpl(false);
  };

  const filtered = rows.filter((r) => (salon === "all" || r.tenant?.id === salon) && (!onlyUnread || r.unread));

  const isCustomer = view === "customer";

  return (
    <div data-testid="aw-lead-inbox" className="h-full flex flex-col min-h-0">
      <div className="flex items-center gap-2 mb-4 flex-wrap flex-shrink-0">
        <h1 className="font-display text-2xl font-black mr-2">Inbox</h1>
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] overflow-hidden">
          <button data-testid="inbox-view-lead" onClick={() => { setView("lead"); setSel(null); setMessages([]); }}
            className={`px-3 py-1.5 text-xs font-bold ${!isCustomer ? "bg-[color:var(--sp-brand)] text-white" : "bg-white"}`}>Lead</button>
          <button data-testid="inbox-view-customer" onClick={() => { setView("customer"); setSel(null); setMessages([]); }}
            className={`px-3 py-1.5 text-xs font-bold ${isCustomer ? "bg-[color:var(--sp-brand)] text-white" : "bg-white"}`}>Clienti</button>
        </div>
        <select data-testid="lead-inbox-salon-filter" className="sp-input !w-auto !py-1.5 text-sm"
          value={salon} onChange={(e) => setSalon(e.target.value)}>
          <option value="all">Tutti i miei saloni</option>
          {availableTenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button onClick={() => setOnlyUnread((v) => !v)} data-testid="lead-inbox-unread-filter"
          className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full ${onlyUnread ? "bg-[color:var(--sp-brand)] text-white" : "bg-white border border-[color:var(--sp-border)]"}`}>
          Nuove risposte
        </button>
        <button onClick={load} className="sp-btn-ghost inline-flex items-center gap-1 text-sm ml-auto"><RefreshCw className="h-4 w-4" /></button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 flex-1 min-h-0">
        {/* Lista conversazioni */}
        <div className="sp-card overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-[color:var(--sp-text-muted)]">Carico…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-[color:var(--sp-text-muted)]">{isCustomer ? "Nessuna conversazione cliente." : "Nessun lead in attesa di risposta."}</div>
          ) : (
            <div className="divide-y divide-[color:var(--sp-border)] max-h-[70vh] overflow-y-auto" data-testid="lead-conv-list">
              {filtered.map((r) => (
                <button key={`${r.tenant?.id}-${r.thread_key}`} onClick={() => openConv(r)}
                  data-testid={`lead-conv-${r.thread_key}`}
                  className={`w-full text-left px-3 py-2.5 hover:bg-[color:var(--sp-subtle)] flex items-center gap-2 ${sel?.thread_key === r.thread_key ? "bg-[color:var(--sp-brand)]/5" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <TenantChip name={r.tenant?.name || "—"} />
                      <span className="text-[10px] text-[color:var(--sp-text-muted)] ml-auto">{fmt(r.last_timestamp)}</span>
                    </div>
                    <div className={`text-sm truncate ${r.unread ? "font-black" : "font-bold"}`}>{r.client?.name || "Sconosciuto"}</div>
                    <div className={`text-xs truncate ${r.unread ? "text-[color:var(--sp-text)] font-semibold" : "text-[color:var(--sp-text-muted)]"}`}>{r.last_message || "—"}</div>
                    <div className="flex gap-1 mt-1 items-center">
                      {!isCustomer && <Badge badge={getAiBadge(r)} />}
                      {!isCustomer && cbByClient[r.thread_key] && (
                        <span data-testid={`lead-conv-callback-${r.thread_key}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold bg-cyan-100 text-cyan-700 border-cyan-200">
                          <PhoneCall className="h-2.5 w-2.5" /> {fmtTime(cbByClient[r.thread_key].scheduled_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  {r.unread && <span className="h-3 w-3 rounded-full bg-[#25D366] flex-shrink-0" data-testid={`lead-conv-unread-${r.thread_key}`} title="Non letto" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversazione */}
        <div className="sp-card flex flex-col h-full min-h-0">
          {!sel ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[color:var(--sp-text-muted)]">Seleziona una conversazione</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[color:var(--sp-border)] flex-shrink-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] font-black text-xs" data-testid="conv-salon-label">
                    <Store className="h-3.5 w-3.5" /> {sel.tenant?.name || "—"}
                  </span>
                  <span className="font-bold truncate">{sel.client?.name}</span>
                  <Badge badge={getAiBadge(sel)} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid="lead-mark-unread" onClick={markUnread}
                    className="sp-btn-ghost inline-flex items-center gap-1 text-xs"><MailOpen className="h-3.5 w-3.5" /> Segna non letto</button>
                  {!isCustomer && sel.agency_case_id && (
                    <AiStatusControl caseId={sel.agency_case_id} status={sel.ai_status}
                      onChanged={(next) => { setSel((s) => ({ ...s, ai_status: next })); load(); }} />
                  )}
                  {!isCustomer && (
                    <button data-testid="lead-schedule-callback" onClick={() => setShowCallback(true)}
                      className="sp-btn-ghost inline-flex items-center gap-1 text-xs"><PhoneCall className="h-4 w-4" /> Richiamo</button>
                  )}
                  <button data-testid="lead-open-drawer" onClick={() => setShowDrawer(true)}
                    className="sp-btn-ghost inline-flex items-center gap-1 text-xs ml-auto"><IdCard className="h-4 w-4" /> Scheda</button>
                </div>
              </div>
              <MessageList messages={messages} testId="lead-messages" emptyText="Nessun messaggio." />
              <div className="p-3 border-t border-[color:var(--sp-border)] flex-shrink-0">
                {!windowOpen && (
                  <div className="mb-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="lead-window-closed">
                    Finestra 24h chiusa: puoi inviare solo un template approvato del salone.
                  </div>
                )}
                {pendingTpl && (
                  <div className="mb-2 flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-2 py-1.5" data-testid="lead-pending-template">
                    <FileText className="h-3.5 w-3.5 flex-shrink-0" /> Template pronto: <span className="font-bold truncate">{pendingTpl}{pendingTplLang ? ` · ${pendingTplLang}` : ""}</span>
                    <button onClick={() => { setPendingTpl(null); setPendingTplLang(""); setPendingTplVars([]); setReply(""); }} className="ml-auto text-emerald-700 hover:text-emerald-900" data-testid="lead-pending-template-clear" title="Annulla template"><X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                {pendingTplVars.length > 0 && (
                  <div className="mb-2 space-y-1" data-testid="lead-template-vars">
                    {pendingTplVars.map((v, i) => (
                      <input key={i} data-testid={`lead-template-var-${i + 1}`} className="sp-input text-xs"
                        placeholder={`Variabile {{${i + 1}}}`} value={v}
                        onChange={(e) => setPendingTplVars(pendingTplVars.map((x, j) => (j === i ? e.target.value : x)))} />
                    ))}
                  </div>
                )}
                <div className="flex gap-2 items-center relative">
                  <button type="button" data-testid="lead-templates-btn" onClick={() => setShowTpl((v) => !v)}
                    className="inline-flex items-center justify-center h-9 w-9 flex-shrink-0 rounded-lg border border-[color:var(--sp-border)] text-emerald-600 hover:bg-emerald-50" title="Template WhatsApp"><FileText className="h-5 w-5" /></button>
                  {showTpl && (
                    <div className="absolute bottom-12 left-0 z-30 w-72 max-h-64 overflow-auto sp-card p-2 shadow-lg" data-testid="lead-templates-popover">
                      <div className="text-[10px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] px-1 mb-1">Template approvati sul numero in uso</div>
                      {templates.length === 0 ? (
                        <div className="text-xs text-[color:var(--sp-text-muted)] italic px-1 py-2" data-testid="lead-no-templates">
                          {LEAD_TPL_REASON[tplReason] || LEAD_TPL_REASON.NO_APPROVED_TEMPLATES_ON_WABA}
                        </div>
                      ) : templates.map((t) => (
                        <button key={`${t.name}|${t.language}`} data-testid={`lead-template-${t.name}`} onClick={() => selectTemplate(t)}
                          className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[color:var(--sp-subtle)]">
                          <div className="text-xs font-bold truncate">{t.name}</div>
                          <div className="text-[10px] text-[color:var(--sp-text-muted)] truncate">{t.category || ""}{t.language ? ` · ${t.language}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  <input data-testid="lead-reply-input" className="sp-input flex-1"
                    placeholder={pendingTpl ? "" : (windowOpen ? "Scrivi un messaggio…" : "Finestra 24h chiusa — usa un template")}
                    value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                    readOnly={!!pendingTpl} disabled={!windowOpen && !pendingTpl} />
                  <button data-testid="lead-send-btn" onClick={send} disabled={sending || !reply.trim() || (!windowOpen && !pendingTpl)}
                    className="sp-btn-primary inline-flex items-center gap-1 disabled:opacity-50"><Send className="h-4 w-4" /></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showDrawer && sel && convClient && (
        <ApiProvider client={convClient}>
          <ContactDrawer
            clientId={sel.thread_key}
            tenant={sel.tenant}
            caseObj={sel.agency_case_id ? { id: sel.agency_case_id, operational_status: sel.operational_status, ai_status: sel.ai_status } : null}
            onClose={() => setShowDrawer(false)}
            onUpdated={() => { load(); loadCallbacks(); }}
          />
        </ApiProvider>
      )}
      {showCallback && sel && (
        <CallbackModal
          tenant={sel.tenant}
          clientId={sel.thread_key}
          clientName={sel.client?.name}
          caseObj={sel.agency_case_id ? { id: sel.agency_case_id } : null}
          onClose={() => setShowCallback(false)}
          onSaved={() => { loadCallbacks(); }}
        />
      )}
    </div>
  );
}
